'use strict';

import { clone } from 'underscore';
import { sprintf } from 'sprintf-js';

/**
 * Implementation of bitmaps of any size.
 *
 * Can be initialized with an array of integers where the array, left to right, represents the bits, right to left.
 *
 * Bitwise operator notes:
 *
 *  Setting a bit
 *      Use the bitwise OR operator (|) to set a bit.
 *
 *      number |= 1 << x;
 *      That will set bit x.
 *
 *  Clearing a bit
 *
 *      Use the bitwise AND operator (&) to clear a bit.
 *
 *      number &= ~(1 << x);
 *      That will clear bit x. You must invert the bit string with the bitwise NOT operator (~), then AND it.
 *
 *  Toggling a bit
 *
 *      The XOR operator (^) can be used to toggle a bit.
 *
 *      number ^= 1 << x;
 *      That will toggle bit x.
 *
 *  Checking a bit
 *
 *      To check a bit, shift the number x to the right, then bitwise AND it:
 *
 *      bit = (number >> x) & 1;
 *      That will put the value of bit x into the variable bit.
 *
 *  Changing the nth bit to x
 *
 *      Setting the nth bit to either 1 or 0 can be achieved with the following:
 *
 *      number ^= (-x ^ number) & (1 << n);
 *      Bit n will be set if x is 1, and cleared if x is 0.
 *
 *  Also see ... https://miguelmota.com/blog/bitwise-operators-in-javascript/
 */
const Bitmap = function(bitmaps) {
    return ({
        /* Storage for sub-bitmaps. Initialized to a single chunk == 0 */
        _bitmaps: bitmaps ? clone(bitmaps) : [0],

        /* The number of bits that will fit into a sub-bitmap.
         * Tried this as (Number.MAX_SAFE_INTEGER).toString(2).length
         * but the bitwise operators only work correctly for signed integers, so setting the chunk size accordingly.
         */
        CHUNK_SIZE: 31,

        /**
         *
         * @param offset
         * @param value
         */
        setBit: function(offset, value) {
            let index = this._getBitmapElementForWrite(offset);
            let bm = this._bitmaps[index];

            // Calculate the offset within the current chunk
            let o = offset - (index * (this.CHUNK_SIZE));

            if (value) {
                bm |= 1 << o;
            } else {
                bm &= ~(1 << o);
            }
            this._bitmaps[index] = bm;

            //  For debugging ...
            //console.log(`Op: ${value}  Index: ${index}  Offset: ${offset}`);
            //console.log(this._bitmaps);
            //console.log(`${this.toString(2)} `);
        },

        /**
         * Toggle a bit.
         */
        toggleBit() {
            throw "Not implemented.";
        },

        /**
         * Checks the values for this Bitmap to another Bitmap and returns true if they are equal.
         * Otherwise, false.
         */
        equals: function(otherBitmap) {
            if (! otherBitmap) {
                return false;
            }

            // check for some specific elements of a Bitmap to make reasonably sure the argument is a Bitmap
            if (! ((typeof otherBitmap === 'object') && otherBitmap.getValue && (typeof otherBitmap.getValue === 'function'))) {
                return false;
            }

            //  Get the array of values for the individual bitmaps.
            let bitmaps1 = this.getValue();
            let bitmaps2 = otherBitmap.getValue();

            //  Determine which array has the greatest length.
            let length = (bitmaps1.length >= bitmaps2) ? bitmaps1.length : bitmaps2.length;

            let isEqual = true;
            for (let i = 0; i < length; i++) {
                //  Set the array to [0] if it doesn't exist.
                let bm1 = (bitmaps1[i] ? bitmaps1[i] : 0);
                let bm2 = (bitmaps2[i] ? bitmaps2[i] : 0);
                if (bm1 !== bm2) {
                    isEqual = false;
                    break;
                }
            }
            return(isEqual);
        },

        /**
         * Returns true if the Bitmaps value is zero. (This is a little esoteric, but helpful for our usage)
         */
        isZero: function() {
            let isZero = true;
            for (let i = 0; i < this._bitmaps.length; i++) {
                if (this._bitmaps[i] > 0) {
                    isZero = false;
                    break;
                }
            }
            return(isZero);
        },

        /**
         * Returns true if a single bit is set. Otherwise, false. (Again, a little esoteric, but helpful for our usage)
         */
        isSingleBitSet: function() {
            let isSingleBitSet = false;
            for (let i = 0; i < this._bitmaps.length; i++) {
                let bm = this._bitmaps[i];
                if (bm > 0) {
                    // If a single bit has already been matched then any other matches should result in a false.
                    if (isSingleBitSet) {
                        isSingleBitSet = false;
                        break;
                    }
                    //  The (bm == (bm & -bm)) part is a very clever, internet-sourced test for a single bit being set.
                    if (bm === (bm & -bm)) {
                        isSingleBitSet = true;
                    }
                }
            }
            return(isSingleBitSet);
        },

        /**
         * Returns the current capacity in bits.
         */
        _getCurrentCapacity: function() {
            return(this._bitmaps.length * (this.CHUNK_SIZE));
        },

        _getBitmapElementForWrite: function(offset) {
            /*
             * Grow the bitmaps array until there are enough bits to accommodate the offset.
             *
             * Go ahead and set the index if the bitmap array needs to be grown to avoid looping to find the
             * array element below.
             *
             * TODO: Maybe set a maximum size.
             */
            let index = 0;

            //  Have to add 1 to the offset since it include the zero bit.
            while (this._getCurrentCapacity() < (offset + 1)) {
                index = ~~(this._getCurrentCapacity() / this.CHUNK_SIZE);
                this._bitmaps.push(0);
            }

            if (index === 0) {
                //  The ~~ part is a faster Floor().
                let chunkCount = ~~(this._getCurrentCapacity() / this.CHUNK_SIZE);
                for (let i = 0; i < chunkCount; i++) {
                    if ((offset + 1) <= ((i + 1) * this.CHUNK_SIZE)) {
                        index = i;
                        break;
                    }
                }
            }

            return(index);
        },

        /**
         * Gets a copy of the internal bitmap array.
         */
        getValue: function() {
            return(clone(this._bitmaps));
        },

        /**
         * Returns a string representation of the bitmap.
         */
        toString: function() {
            let out = "";

            for (let i = this._bitmaps.length - 1; i >= 0; i--) {
                let bm = this._bitmaps[i];
                let padding = this.CHUNK_SIZE;
                let chunk = sprintf("%0" + padding + "s", (bm).toString(2));
                out = out + chunk;
            }

            return(out);
        }
    });
};

/**
 * Performs a bitwise "and" comparison and returns the resulting bitmap.
 */
Bitmap.and = function(bitmap1, bitmap2) {
    if (! bitmap1 || ! bitmap2) {
        throw "Invalid arguments";
    }

    //  Get the array of values for the individual bitmaps.
    let bitmaps1 = bitmap1.getValue();
    let bitmaps2 = bitmap2.getValue();

    //  Determine which array has the greatest length.
    let length = (bitmaps1.length >= bitmaps2) ? bitmaps1.length : bitmaps2.length;

    let newValues = [];
    for (let i = 0; i < length; i++) {
        //  Set the array to [0] if it doesn't exist.
        let bm1 = (bitmaps1[i] ? bitmaps1[i] : 0);
        let bm2 = (bitmaps2[i] ? bitmaps2[i] : 0);
        //  and the two together are store.
        newValues.push(bm1 & bm2);
    }
    return(new Bitmap(newValues));
};

/**
 * Performs a bitwise "xor" comparison and returns the resulting bitmap.
 */
Bitmap.xor = function(bitmap1, bitmap2) {
    if (! bitmap1 || ! bitmap2) {
        throw "Invalid arguments";
    }

    //  Get the array of values for the individual bitmaps.
    let bitmaps1 = bitmap1.getValue();
    let bitmaps2 = bitmap2.getValue();

    //  Determine which array has the greatest length.
    let length = (bitmaps1.length >= bitmaps2) ? bitmaps1.length : bitmaps2.length;

    let newValues = [];
    for (let i = 0; i < length; i++) {
        //  Set the array to [0] if it doesn't exist.
        let bm1 = (bitmaps1[i] ? bitmaps1[i] : 0);
        let bm2 = (bitmaps2[i] ? bitmaps2[i] : 0);
        //  and the two together are store.
        newValues.push(bm1 ^ bm2);
    }
    return(new Bitmap(newValues));
};

export default Bitmap;